import { FC, FormEvent, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import LockResetIcon from '@mui/icons-material/LockReset'
import BadgeIcon from '@mui/icons-material/Badge'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import { authService } from '@/services/authService'
import { perfilService, PerfilRequest } from '@/services/perfilService'
import { PreferenciasNotificacaoCard } from '@/components/perfil/PreferenciasNotificacaoCard'
import { CardMeuNivel } from '@/components/nivel/CardMeuNivel'
import { permissoesNivelConsultor } from '@/utils/nivelConsultor'

/** Comprimento mínimo da nova senha — espelha o @Size do ChangePasswordRequest no backend. */
const MIN_SENHA = 8

/** O interceptor do axios rejeita com { message, ... } (não um Error). */
const messageOf = (e: unknown, fallback: string): string =>
  (e as { message?: string })?.message ?? fallback

/** Iniciais (1ª + última palavra) para o avatar. */
const iniciais = (nome?: string): string => {
  if (!nome) return '?'
  const partes = nome.trim().split(/\s+/)
  const a = partes[0]?.[0] ?? ''
  const b = partes.length > 1 ? partes[partes.length - 1][0] : ''
  return (a + b).toUpperCase() || '?'
}

type CampoSenha = 'atual' | 'nova' | 'confirmar'

const PerfilPage: FC = () => {
  const { usuario } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  // Perfil (dados cadastrais e profissionais) — mantidos pelo próprio usuário.
  const [form, setForm] = useState<PerfilRequest>({})
  const [carregandoPerfil, setCarregandoPerfil] = useState(true)
  const [salvandoPerfil, setSalvandoPerfil] = useState(false)
  const [erroPerfil, setErroPerfil] = useState<string | null>(null)

  const [senhaAtual, setSenhaAtual] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmar, setConfirmar] = useState('')
  const [show, setShow] = useState<Record<CampoSenha, boolean>>({
    atual: false,
    nova: false,
    confirmar: false,
  })
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const novaCurta = novaSenha.length > 0 && novaSenha.length < MIN_SENHA
  const naoConfere = confirmar.length > 0 && confirmar !== novaSenha
  const igualAtual = novaSenha.length > 0 && novaSenha === senhaAtual
  const podeSalvar =
    senhaAtual.length > 0 &&
    novaSenha.length >= MIN_SENHA &&
    confirmar === novaSenha &&
    !igualAtual &&
    !salvando

  const toggle = (campo: CampoSenha) => () =>
    setShow((s) => ({ ...s, [campo]: !s[campo] }))

  const adornment = (campo: CampoSenha, visivel: boolean) => (
    <InputAdornment position="end">
      <IconButton onClick={toggle(campo)} edge="end" size="small" tabIndex={-1}>
        {visivel ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  )

  const trocarSenha = async (e: FormEvent) => {
    e.preventDefault()
    if (!podeSalvar) return
    setErro(null)
    setSalvando(true)
    try {
      await authService.changePassword({ senhaAtual, novaSenha })
      enqueueSnackbar('Senha alterada com sucesso', { variant: 'success' })
      setSenhaAtual('')
      setNovaSenha('')
      setConfirmar('')
    } catch (err) {
      setErro(messageOf(err, 'Não foi possível alterar a senha.'))
    } finally {
      setSalvando(false)
    }
  }

  const carregarPerfil = useCallback(async () => {
    setCarregandoPerfil(true)
    setErroPerfil(null)
    try {
      const p = await perfilService.obter()
      // Só os campos editáveis entram no formulário; identidade é leitura.
      const { id: _id, nome: _n, email: _e, cpf: _c, cnpj: _cn, tenantId: _t, tipoCanal: _tc, ...editaveis } = p
      setForm(editaveis)
    } catch (err) {
      setErroPerfil(messageOf(err, 'Não foi possível carregar seus dados.'))
    } finally {
      setCarregandoPerfil(false)
    }
  }, [])

  useEffect(() => {
    void carregarPerfil()
  }, [carregarPerfil])

  const campo =
    (nome: keyof PerfilRequest) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [nome]: e.target.value }))

  const salvarPerfil = async () => {
    setSalvandoPerfil(true)
    setErroPerfil(null)
    try {
      await perfilService.salvar(form)
      enqueueSnackbar('Dados salvos', { variant: 'success' })
    } catch (err) {
      setErroPerfil(messageOf(err, 'Não foi possível salvar seus dados.'))
    } finally {
      setSalvandoPerfil(false)
    }
  }

  const permissoes = usuario?.permissoes ?? []
  const veMeuNivel = permissoesNivelConsultor(usuario?.permissoes).veMeuNivel

  return (
    <Box>
      <PageHeader title="Meu perfil" subtitle="Seus dados de conta e troca de senha" />

      <Stack spacing={3} sx={{ maxWidth: 560 }}>
        {/* ─── Dados da conta ─── */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, fontWeight: 700 }}>
              {iniciais(usuario?.nome)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {usuario?.nome ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {usuario?.email ?? '—'}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Perfis de acesso
              </Typography>
              <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {permissoes.length > 0 ? (
                  permissoes.map((p) => (
                    <Chip key={p} label={p} size="small" color="primary" variant="outlined" />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Stack>
            </Box>
            {usuario?.tipoCanal ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Canal
                </Typography>
                <Typography variant="body2">{usuario.tipoCanal}</Typography>
              </Box>
            ) : null}
          </Stack>
        </Paper>

        {/* ─── Meu nível (AN-10): só a persona CONSULTOR; só informativo ─── */}
        {veMeuNivel && <CardMeuNivel />}

        {/* ─── Dados cadastrais e profissionais ─── */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <BadgeIcon color="action" />
            <Typography fontWeight={600}>Dados cadastrais e profissionais</Typography>
          </Stack>

          {erroPerfil && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erroPerfil}
            </Alert>
          )}

          {carregandoPerfil ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Celular / WhatsApp"
                  value={form.celular ?? ''}
                  onChange={campo('celular')}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Telefone"
                  value={form.telefone ?? ''}
                  onChange={campo('telefone')}
                  fullWidth
                  size="small"
                />
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Endereço
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="CEP"
                  value={form.cep ?? ''}
                  onChange={campo('cep')}
                  size="small"
                  sx={{ width: 140 }}
                  helperText="8 dígitos"
                />
                <TextField
                  label="Logradouro"
                  value={form.logradouro ?? ''}
                  onChange={campo('logradouro')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Nº"
                  value={form.numero ?? ''}
                  onChange={campo('numero')}
                  size="small"
                  sx={{ width: 100 }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Complemento"
                  value={form.complemento ?? ''}
                  onChange={campo('complemento')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Bairro"
                  value={form.bairro ?? ''}
                  onChange={campo('bairro')}
                  size="small"
                  fullWidth
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Cidade"
                  value={form.cidade ?? ''}
                  onChange={campo('cidade')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="UF"
                  value={form.uf ?? ''}
                  onChange={campo('uf')}
                  size="small"
                  sx={{ width: 90 }}
                  inputProps={{ maxLength: 2 }}
                />
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Dados bancários (recebimento de comissão)
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Banco"
                  value={form.banco ?? ''}
                  onChange={campo('banco')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Agência"
                  value={form.agencia ?? ''}
                  onChange={campo('agencia')}
                  size="small"
                  sx={{ width: 140 }}
                />
                <TextField
                  label="Conta"
                  value={form.conta ?? ''}
                  onChange={campo('conta')}
                  size="small"
                  sx={{ width: 160 }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  label="Tipo de conta"
                  value={form.tipoConta ?? ''}
                  onChange={campo('tipoConta')}
                  size="small"
                  sx={{ width: 180 }}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">—</option>
                  <option value="CORRENTE">Corrente</option>
                  <option value="POUPANCA">Poupança</option>
                </TextField>
                <TextField
                  label="Chave PIX"
                  value={form.chavePix ?? ''}
                  onChange={campo('chavePix')}
                  size="small"
                  fullWidth
                />
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Profissional
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Formação"
                  value={form.formacao ?? ''}
                  onChange={campo('formacao')}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="E-mail profissional"
                  value={form.emailProfissional ?? ''}
                  onChange={campo('emailProfissional')}
                  size="small"
                  fullWidth
                />
              </Stack>
              <TextField
                label="Histórico profissional"
                value={form.historicoProfissional ?? ''}
                onChange={campo('historicoProfissional')}
                size="small"
                fullWidth
                multiline
                minRows={3}
              />

              <Box>
                <Button variant="contained" onClick={salvarPerfil} disabled={salvandoPerfil}>
                  {salvandoPerfil ? 'Salvando…' : 'Salvar dados'}
                </Button>
              </Box>
            </Stack>
          )}
        </Paper>

        {/* ─── Avisos por e-mail (Fase 6, item 23) ─── */}
        <PreferenciasNotificacaoCard />

        {/* ─── Alterar senha ─── */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <LockResetIcon color="action" />
            <Typography fontWeight={600}>Alterar senha</Typography>
          </Stack>

          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}

          <Stack spacing={2} component="form" onSubmit={trocarSenha}>
            <TextField
              label="Senha atual"
              type={show.atual ? 'text' : 'password'}
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              autoComplete="current-password"
              fullWidth
              InputProps={{ endAdornment: adornment('atual', show.atual) }}
            />
            <TextField
              label="Nova senha"
              type={show.nova ? 'text' : 'password'}
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              autoComplete="new-password"
              fullWidth
              error={novaCurta || igualAtual}
              helperText={
                igualAtual
                  ? 'A nova senha deve ser diferente da atual'
                  : novaCurta
                    ? `Mínimo de ${MIN_SENHA} caracteres`
                    : ' '
              }
              InputProps={{ endAdornment: adornment('nova', show.nova) }}
            />
            <TextField
              label="Confirmar nova senha"
              type={show.confirmar ? 'text' : 'password'}
              value={confirmar}
              onChange={(e) => setConfirmar(e.target.value)}
              autoComplete="new-password"
              fullWidth
              error={naoConfere}
              helperText={naoConfere ? 'As senhas não conferem' : ' '}
              InputProps={{ endAdornment: adornment('confirmar', show.confirmar) }}
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<LockResetIcon />}
              disabled={!podeSalvar}
              sx={{ alignSelf: 'flex-start' }}
            >
              {salvando ? 'Salvando…' : 'Alterar senha'}
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Verificação em duas etapas e códigos de recuperação ficam em{' '}
            <Link component={RouterLink} to="/usuarios/meu-acesso?aba=seguranca">
              Segurança
            </Link>
            .
          </Typography>
        </Paper>
      </Stack>
    </Box>
  )
}

export default PerfilPage
